import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { SafetyTestCategory } from '../data-models/safety-test-category.model';
import { SafetyTestCategoryCreateRequest } from '../request-models/safety-test-category-create-request.model';
import { SafetyTestCategoryUpdateRequest } from '../request-models/safety-test-category-update-request.model';
import { toSafetyTestCategoryCreateRequest, toSafetyTestCategoryRequest } from '../utils/safety-test-request-utils';

/** Filters for `GET /admin/safety-test-categories`. */
export interface SafetyTestCategoryListFilters {
  organizationId?: string;
  systemOnly?: boolean;
}

/**
 * Safety-test categories: `/admin/safety-test-categories` (hub `admin-safety-test-categories-controller.ts`).
 * Whether a category has tests is `SafetyTestService.hasTestsInCategory()`. Failures reject with an `ApiError`.
 */
@Injectable({ providedIn: 'root' })
export class SafetyTestCategoryService extends BaseService<SafetyTestCategory> {
  constructor() {
    super('admin/safety-test-categories', SafetyTestCategory);
  }

  /** GET /admin/safety-test-categories. Blank filters are not sent. */
  listCategories(filters: SafetyTestCategoryListFilters = {}): Promise<SafetyTestCategory[]> {
    const query = { organizationId: filters.organizationId || undefined, systemOnly: filters.systemOnly || undefined };
    return firstValueFrom(this.getMany({ query }));
  }

  /** GET /admin/safety-test-categories/:id */
  getCategory(categoryId: string): Promise<SafetyTestCategory | null> {
    return firstValueFrom(this.get(categoryId));
  }

  /** POST /admin/safety-test-categories (`name` and `shortName` required). */
  createCategory(data: SafetyTestCategoryCreateRequest): Promise<SafetyTestCategory | null> {
    return firstValueFrom(this.create(toSafetyTestCategoryCreateRequest(data)));
  }

  /** PUT /admin/safety-test-categories/:id with only the writable fields that are set. */
  updateCategory(categoryId: string, data: SafetyTestCategoryUpdateRequest): Promise<SafetyTestCategory | null> {
    return firstValueFrom(this.update(categoryId, toSafetyTestCategoryRequest(data)));
  }

  /** DELETE /admin/safety-test-categories/:id (soft delete). */
  deleteCategory(categoryId: string): Promise<void> {
    return firstValueFrom(this.delete(categoryId));
  }
}
